import { useEffect, useMemo, useState } from 'react'
import MainLayout from '../../layouts/MainLayout'
import PageHeader from '../../components/PageHeader'
import { usePermissions } from '../../hooks/usePermissions'
import { useNavigate } from 'react-router-dom'
import useLoader from '../../hooks/useLoader'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import useBaseService from '../../hooks/services/useBaseService'
import { faClose, faEye } from '@fortawesome/free-solid-svg-icons'
import { entities } from '../../constants/entities'
import useAction from '../../hooks/useAction'
import ListHeader from '../../components/ListHeader'
import useUrlFilters from '../../hooks/useUrlFilters'
import useGenericFilter from '../../hooks/filters/useGenericFilter'
import AutoCompleteFilter from '../../components/AutoCompleteFilter'
import baseService from '../../services/baseService'
import useFilterHandlers from '../../hooks/useFilterHandlers'
import { transformFiltersForUrl } from '../../utils/transformFiltersForUrl'
import TableWithInactiveFilter from '../../components/table/TableWithInactiveFilter'
import InputSelect from '../../components/InputSelect'
import FilterCollapse from '../../components/FilterCollapse'
import CloseMaintenance from './components/CloseMaintenance'
import useQueryWithLoader from '../../hooks/useQueryWithLoader'
import { maintenanceStatusColorMap } from '../../utils/statusColorMap'
import { useQueryClient } from '@tanstack/react-query'

const MaintenancePage = () => {
  const navigate = useNavigate()
  const { canAccess } = usePermissions()
  const { showLoader, hideLoader } = useLoader()
  const { get: baseServiceGet } = useBaseService(navigate)
  const queryClient = useQueryClient()
  const [statusMaintenance, setStatusMaintenance] = useState('atendidas')

  // Controlar o modal de fechamento da manutenção
  const [openCloseModal, setOpenCloseModal] = useState(false)
  const [maintenanceSelected, setMaintenanceSelected] = useState(false)

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      'equipment.number': [],
      'latestItem.technicianStatus.name': [],
      deleted_at: false,
    },
    'maintenance'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  // --- Gerenciamento de Filtros Locais do Formulário ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        'equipment.number': [],
        'latestItem.technicianStatus.name': [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const {
    openModalConfirmation,
    action,
    // showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate)

  const {
    data: maintenancesData,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    // 🆕 statusMaintenance entra na queryKey pra refazer o fetch ao trocar
    queryKey: ['maintenance', filters, pagination, statusMaintenance],
    queryFn: async () => {
      showLoader()
      const filtersTransformed = transformFiltersForUrl({
        ...filters,
        latestStatus: statusMaintenance, // 🆕 filtro agora vai pro backend
      })
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: 'created_at',
          order: 'desc',
        },
      }
      const response = await baseServiceGet(entities.maintenances.get, params)
      return response.result
    },
    // 🆕 Política de cache: sempre buscar dados frescos ao entrar na página
    staleTime: 0, // nunca considera o cache "fresco"
    refetchOnMount: 'always', // refetch toda vez que a página monta (mesmo com cache)
    refetchOnWindowFocus: true, // refetch ao voltar pra aba/janela
    select: (data) => {
      return {
        maintenances: (data.data ?? []).map((maintenance) => {
          return {
            ...maintenance,
            maintenance_status:
              maintenance?.latest_item?.maintenance_status?.name ??
              'MANUTENÇÃO AGUARDANDO ATENDIMENTO',
            equipment: maintenance?.equipment
              ? `${maintenance?.equipment?.number} - ${maintenance?.equipment?.name}`
              : '-',
            product: maintenance?.product_item?.product
              ? `${maintenance?.product_item?.code} - ${maintenance.product_item?.product?.name}`
              : '-',
            technician: maintenance?.latest_item?.technician?.name ?? '-',
            technician_status_id:
              maintenance?.latest_item?.technician_status?.id,
            technician_status:
              maintenance?.latest_item?.technician_status?.name ?? '-',
            maintenance_type:
              maintenance?.latest_item?.maintenance_type?.name ?? '-',
            isDeleted: !!maintenance?.deleted_at,
            status: maintenance?.is_closed ? 'FECHADO' : 'EM ABERTO',
          }
        }),
        totalPages: data.last_page ?? 1,
      }
    },
  })

  useEffect(() => {
    if (!isLoading && !isFetching && maintenancesData) {
      hideLoader()
    }
  }, [maintenancesData, isLoading, isFetching, hideLoader])

  // 🆕 usa direto o retorno do backend — já vem filtrado e paginado corretamente
  const { maintenances = [], totalPages = 1 } = maintenancesData || {}

  // ❌ removido: useState(filteredMaintenances) e o useEffect que filtrava em memória

  const hasEquipment = maintenances.some(
    (m) => m.equipment && m.equipment !== '-'
  )

  const hasProduct = maintenances.some((m) => m.product && m.product !== '-')

  const columns =
    statusMaintenance === 'atendidas'
      ? [
          ...(hasEquipment
            ? [
                {
                  key: 'equipment',
                  label: 'Equipamento',
                  align: 'left',
                  width: '30%',
                },
              ]
            : []),

          ...(hasProduct
            ? [{ key: 'product', label: 'Insumo', align: 'left' }]
            : []),
          { key: 'maintenance_date', label: 'Data da Manutenção' },
          { key: 'maintenance_status', label: 'Status da Manutenção' },
          { key: 'technician', label: 'Técnico', align: 'left' },
          {
            key: 'technician_status',
            label: 'Situação Eq.',
            align: 'left',
          },
          { key: 'status', label: 'Status', align: 'left' },
          {
            key: 'maintenance_type',
            label: 'Tipo de Manutenção',
            align: 'left',
          },
        ]
      : [
          ...(hasEquipment
            ? [{ key: 'equipment', label: 'Equipamento', align: 'left' }]
            : []),

          ...(hasProduct
            ? [{ key: 'product', label: 'Insumo', align: 'left' }]
            : []),

          { key: 'maintenance_date', label: 'Data da Manutenção' },
          { key: 'maintenance_status', label: 'Status da Manutenção' },
        ]

  const inputsfilters = useMemo(
    () => [
      // Filtro local, somente para separar manutenções parciais, e atendidas
      {
        label: 'Status da Manutenção (Atendidas/Pendentes)',
        component: (
          <InputSelect
            id="status"
            value={statusMaintenance}
            onChange={(e) => {
              setStatusMaintenance(e.target.value)
              // 🆕 reseta pra página 1 ao trocar o filtro de status
              setPagination({ page: 1, perPage: pagination.perPage })
            }}
            options={[
              { value: 'atendidas', label: 'Atendidas' },
              { value: 'Pendentes', label: 'Pendentes' },
            ]}
          />
        ),
      },
      // Filtros de manupulação (Que fazem chamadas para o backend)
      {
        label: 'Situação',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="name"
            model="technicianStatus"
            value={localFilterValues['latestItem.technicianStatus.name']}
            onChange={(selected) =>
              handleFilterChange('latestItem.technicianStatus.name', selected)
            }
            placeholder="Filtre as situações da manutenção"
            isMulti
            isClearable
          />
        ),
      },
      {
        label: 'Equipamento (Tombo)',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="number"
            extraFilters={{ is_completed: { $eq: 1 } }}
            model="equipment"
            value={localFilterValues['equipment.number']}
            onChange={(selected) =>
              handleFilterChange('equipment.number', selected)
            }
            placeholder="Filtre os equipamentos pelo tombo"
            isMulti
            isClearable
          />
        ),
      },
    ],
    [
      handleFilterChange,
      localFilterValues,
      statusMaintenance,
      pagination.perPage,
      setPagination,
    ]
  )

  const actions = [
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver manutenções',
      onClick: (maintenance) =>
        navigate(`/manutencao/detalhes/${maintenance.id}`),
      show: (maintenance) =>
        !maintenance.isDeleted && canAccess('Ver manutenções'),
    },
    {
      id: 'close_maintenance',
      icon: faClose,
      title: 'Fechar Manutenção',
      buttonClass: 'btn-warning',
      permission: 'Fechar manutenções',
      onClick: (maintenance) => {
        setMaintenanceSelected(maintenance)
        setOpenCloseModal(true)
      },
      show: (maintenance) =>
        maintenance.maintenance_status !==
          'MANUTENÇÃO AGUARDANDO ATENDIMENTO' &&
        maintenance.status === 'EM ABERTO' &&
        (maintenance.technician_status === 'LIBERADO' ||
          maintenance.technician_status === 'SEM REPARO' ||
          maintenance.technician_status === 'USO INTERNO') &&
        canAccess('Fechar manutenções'),
    },
  ]

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Manutenções"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
          defaultOpen={true}
        />

        <ListHeader
          title="Lista de Manutenção"
          buttonText="Cadastrar manutenção"
          buttonLink="/manutencao/criar"
          canAccess={canAccess}
          permission="Criar manutenções"
        />

        <TableWithInactiveFilter
          data={maintenances}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          rowColorConfig={{
            colorMap: maintenanceStatusColorMap,
            propertyKey: 'status',
          }}
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <CloseMaintenance
          open={openCloseModal}
          onClose={() => {
            setMaintenanceSelected(null)
            setOpenCloseModal(false)
            queryClient.invalidateQueries({ queryKey: ['maintenance'] })
          }}
          maintenance={maintenanceSelected}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.equipment : ''}
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}
export default MaintenancePage
